<template>
  <v-card outlined color="#191919" class="fill-height">
    <v-card-title>{{ $t('orders.shares') }}
      <v-spacer></v-spacer>
    </v-card-title>
    <v-card-text>
      <div>{{ $t('orders.share_help_text') }}</div>
      <div>{{ $t('orders.share_help_text2') }}</div>
      <v-form @submit.prevent ref="form">
        <v-row cols="12">
          
          <v-col :cols="$vuetify.breakpoint.smAndUp ? 6 : 12"
          :class="$vuetify.breakpoint.smAndUp ? '' : 'pb-0'"
          >
            <v-text-field v-model="shareEmail"
                          :disabled="$isImpersonating"
                          :rules="[$rules.required, $rules.email]" 
                          validate-on-blur 
                          :label="$t('orders.share_email_label')"></v-text-field>    
          </v-col>
          <v-col :cols="$vuetify.breakpoint.smAndUp ? 6 : 12"
          :class="$vuetify.breakpoint.smAndUp ? '' : 'pb-5'">
            <menu-date-picker v-model="shareExpiresAt"
                              :disabled="$isImpersonating"
                              :label="$t('orders.share_expires_at')"
                              :rules="[$rules.required]"
                              :allow-past-dates="false"
            ></menu-date-picker>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-0 mt-0">
          <v-btn @click="sendInvitation" small color="primary"
                 :disabled="$isImpersonating"
                 :loading="sendingInvitation">{{$t('orders.send_invitation')}}</v-btn>
          </v-col>
        </v-row>
      </v-form>
        
      
      
    </v-card-text>
    <v-card-text>
      <div class="subtitle-2  white--text">{{ $t('orders.invitations_section_title')}}</div>
      <v-list :two-line="invitations.length === 0 && !loading">
        <share-order-invitation-row v-for="invitation in invitations"
                                    :key="invitation.invitationId" 
                                    :invitation="invitation" 
                                    @click:delete="deleteInvitation">
        </share-order-invitation-row>

        <template v-if="invitations.length === 0 && !loading">
          <v-list-item>
            {{ $t('orders.no_open_invitations') }}
          </v-list-item>
        </template>
        
      </v-list>

      <div class="subtitle-2  white--text">
      {{ $t('orders.shares_section_title')}}
      </div>
      <v-list>
      <share-order-share-row v-for="share in shares" 
                             :key="share.shareId" 
                             :share="share"
                             @click:edit="editShare"
                             @click:delete="deleteShare"></share-order-share-row>
      <template v-if="shares.length === 0 && !loading">
        <v-list-item>
          {{ $t('orders.no_shares') }}
        </v-list-item>
      </template>
      </v-list>
    </v-card-text>
    <share-order-edit-share-dialog ref="editShareDialog" @share-saved="shareSaved"></share-order-edit-share-dialog>
  </v-card>
</template>

<script>
import orderApi from "@/api/orderApi";
import ShareOrderInvitationRow from "@/components/SingleOrderView/ShareOrderInvitationRow.vue";
import ShareOrderShareRow from "@/components/SingleOrderView/ShareOrderShareRow.vue";
import MenuDatePicker from "@/components/MenuDatePicker.vue";
import ShareOrderEditShareDialog from "@/components/SingleOrderView/ShareOrderEditShareDialog.vue";

export default {
  components: {ShareOrderEditShareDialog, MenuDatePicker, ShareOrderShareRow, ShareOrderInvitationRow},
  props: ['order'],
  name: 'ShareOrder',
  data() {
    return {
      roles: [],
      shareEmail: '',
      shareExpiresAt: null,
      menu: false,
      shareRole: '',
      invitations: [],
      shares: [],
      sendingInvitation: false,
      loading: true,
    }
  },
  methods: {
    editShare(share) {
      this.$refs.editShareDialog.open(share);
    },
    shareSaved(share) {
      this.$showSuccessNotification(this.$t('orders.share_updated'));
      this.reload();
    },
    async sendInvitation() {
      this.sendingInvitation = true;
      
      if (!this.$refs.form.validate()) {
        this.sendingInvitation = false;
        return;
      }

      try {
        const newInvitation = await orderApi.addOrderInvitation(this.order.id, { expiresAt: this.shareExpiresAt, email: this.shareEmail, role: 'OMA_ASENTAJA' });
        this.invitations.push(newInvitation);
        this.shareEmail = '';
        this.shareExpiresAt = null;
        this.$showSuccessNotification(this.$t('orders.invitation_sent'));
      } catch (error) {
        this.$handleApiError(error);
      }
      
      this.sendingInvitation = false;
    },
    async deleteInvitation(invitation) {
      this.$set(invitation, '_deleting', true);

      if (!await this.$confirmDelete(this.$t('orders.confirm_delete_invitation_message', { email: invitation.email }), this.$t('orders.confirm_delete_invitation_title'))) {
        this.$set(invitation, '_deleting', false);
        return;
      }
      
      try {
        await orderApi.deleteOrderInvitation(this.order.id, invitation.invitationId);
        this.invitations = this.invitations.filter(i => i.invitationId !== invitation.invitationId);
        this.$showSuccessNotification(this.$t('orders.invitation_deleted'));
      } catch (error) {
        this.$handleApiError(error);
      }
      this.$set(invitation, '_deleting', false);
    },
    async deleteShare(share) {
      this.$set(share, '_deleting', true);

      if (!await this.$confirmDelete(this.$t('orders.confirm_delete_share_message', { email: share.email }), this.$t('orders.confirm_delete_share_title'))) {
        this.$set(share, '_deleting', false);
        return;
      }
      
      try {
        await orderApi.deleteOrderShare(this.order.id, share.shareId);
        this.shares = this.shares.filter(s => s.shareId !== share.shareId);
        this.$showSuccessNotification(this.$t('orders.share_deleted'));
      } catch (error) {
        this.$handleApiError(error);
      }
      this.$set(share, '_deleting', false);
    },
    async reload() {
      this.loading = true;
      try {
        const shares = await orderApi.getOrderShares(this.order.id);
        this.shares = shares;
        const invitations = await orderApi.getOrderInvitations(this.order.id);
        
        this.invitations = invitations.filter(i => i.acceptedAt == null);
        
      } catch (error) {
        this.$handleApiError(error);
      }
      this.loading = false;
    }
  },
  mounted() {
    this.reload();
  }

}
</script>

<style scoped>

</style>
